import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Geolocation, Geoposition, PositionError } from '@awesome-cordova-plugins/geolocation/ngx';
import { Platform } from '@ionic/angular';
import { RxState } from '@rx-angular/state';
import { FrontendMapProvider, IFrontendMapboxConfig, IMapBounds, IInstallation, StateValues } from 'common_library';
import { Geometry, Position } from 'geojson';
import mapboxgl, { GeoJSONSource, LngLatLike, Map, Marker, StyleSpecification } from 'mapbox-gl';
import { Subscription } from 'rxjs';
import { GlobalState, GLOBAL_RX_STATE } from 'src/app/app-state.tokens';
import { _ } from 'src/app/consts';
import { SocketIoService } from 'src/app/services/communication/socket-io.service';
import { environment } from 'src/environments/environment';

import { StoreService } from '../../services/utils/store.service';
import { ToastService } from 'src/app/services/utils/toast.service';
import { Geolocation as Geo } from '@capacitor/geolocation';
import { GeoLocationNativeService } from 'src/app/services/native/geo-location-native.service';
import { TranslateService } from '@ngx-translate/core';
import { MainStateService } from 'src/app/services/state/app-main-state.service';
import { FrontendConfigService } from 'src/app/services/frontend-config.service';
import { TranslateConfigService } from 'src/app/services/translate.service';
import { HttpIoService } from 'src/app/services/communication/http-io.service';

const LEGACY_MAP_STYLE_URL = 'https://tiles.eagleprojects.cloud/styles/basic-preview/style.json';
const DATA_SOURCE_ID = 'dataSource';
const MAPBOX_DEFAULT_STYLE_ID = 'mapbox/streets-v12';
const FORCE_MAPBOX_FALLBACK_STORAGE_KEY = 'yf.forceMapboxFallback';
const DEFAULT_MAP_CENTER: [number, number] = [12.496366, 41.902782];

export interface MapPosition { lat: number, lng: number, showLocationMarker: boolean }
export interface MapItem { type: MapItemType, geometry: Geometry, properties?: InstallationProperties | ClusterProperties }
export enum MapItemType { INSTALLATION, CLUSTER, BACKGROUND }
export interface InstallationProperties { id: string, state: number, power: number, cpo: string, operatorIds?: string[], markerImage?: string }
export interface ClusterProperties { count: number, ids: string[] }
export interface IMapSettings {
  icons: string[]
}

@Component({
    selector: 'app-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    standalone: false
})
export class MapComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  // PRIVATE -------------------------------------------------------------------------------------
  @ViewChild('mapContainer', { static: true }) mapContainer: ElementRef<HTMLDivElement>;

  _ready = false;
  _style: string | StyleSpecification;
  _mapProvider: FrontendMapProvider = 'legacy';
  private _fallbackStyleUrl = LEGACY_MAP_STYLE_URL;
  private _clusterTextFont = ['Noto Sans Regular'];
  private _mapboxFallbackTriggered = false;
  private _viewReady = false;
  private _mapInitializing = false;
  private _nativeWatchId: string | undefined;
  private _webPositionSubscription: Subscription | undefined;
  private _languageSubscription: Subscription | undefined;
  private readonly _staticMarkerImageIds = [
    'yfl_0', 'yfl_1', 'yfl_2', 'yfl_3', 'yfl_4', 'yfl_5',
    'enx_0', 'enx_1', 'enx_2', 'enx_3', 'enx_4', 'enx_5',
    'ftx_0', 'ftx_1', 'ftx_2', 'ftx_3', 'ftx_4', 'ftx_5',
    'plenitude_0', 'plenitude_1', 'plenitude_2', 'plenitude_3', 'plenitude_4', 'plenitude_5',
    'a2a_0', 'a2a_1', 'a2a_2', 'a2a_3', 'a2a_4', 'a2a_5',
    'not_yfl_0', 'not_yfl_1', 'not_yfl_2', 'not_yfl_3', 'not_yfl_4', 'not_yfl_5',
  ];
  private readonly _dynamicMarkerAvailability = new globalThis.Map<string, boolean>();
  private readonly _dynamicMarkerLoads = new globalThis.Map<string, Promise<void>>();
  _zoom = 15;
  _center: [number, number] = DEFAULT_MAP_CENTER;
  /** Override admin della posizione di partenza attivo → niente auto-followMe verso il device. */
  private _startOverrideActive = false;
  _selectedInstallation: IInstallation | undefined;
  _map: Map | undefined;
  _me: Marker | undefined;
  _selectedStationMarker: Marker | undefined;
  _shownMap: IMapBounds;
  _dbto: any;
  _data: GeoJSON.FeatureCollection<GeoJSON.Point> = { type: 'FeatureCollection', features: [] };
  subscription: Subscription;

  geoLocateCtrl: mapboxgl.GeolocateControl;
  geoLocateCtrlHide = true;
  _locationMarker: Marker;

  // PUBLIC -------------------------------------------------------------------------------------

  @Input() data: GeoJSON.FeatureCollection<GeoJSON.Point> = { type: 'FeatureCollection', features: [] };
  @Input() updates: InstallationProperties[] = [];
  @Input() center: MapPosition;
  @Input() selectedInstallation: IInstallation;
  @Input() reload: boolean = false;

  @Output() mapReady = new EventEmitter<boolean>();
  @Output() mapBounds = new EventEmitter<IMapBounds>();
  @Output() clickItem = new EventEmitter<MapItem>();

  readonly native: boolean;  

  constructor(private platform: Platform,
    private geolocation: Geolocation,
    private store: StoreService,
    public SIS: SocketIoService,
    private ngZone: NgZone,
    private toast: ToastService,
    private geoLocationNative: GeoLocationNativeService,
    @Inject(GLOBAL_RX_STATE) private globalState: RxState<GlobalState>,
    private mainStateService: MainStateService,
    private frontendConfigService: FrontendConfigService,
    private translateConfigService: TranslateConfigService,
    private HIO: HttpIoService,
    private translate: TranslateService) {
    this.native = this.mainStateService.getNative();
  }

  async ngOnInit() {
    console.log("map ngOnInit start");
    await Promise.all([
      this.platform.ready(),
      this.configureMapProvider()
    ]);

    this._languageSubscription = this.translate.onLangChange.subscribe(({ lang }) => {
      if (this._mapProvider === 'mapbox') {
        this._map?.setLanguage(this.normalizeMapLanguage(lang));
      }
    });

    // Admin: posizione di partenza forzata (5-tap sulla lente) → vale COME geolocalizzazione,
    // salta il device (l'admin sceglie da dove partire, es. demo da un'altra città).
    const override = this.store.get<[number, number]>(_.MAP_START_OVERRIDE);
    if (this.isAdmin() && this.isValidMapCenter(override)) {
      this._center = override;
      this._startOverrideActive = true; // niente auto-followMe verso il device
      this.store.set(_.GEOLOCATION_KEY, override);
      console.log("map: admin start-override attivo", override);
      this.createMap();
      return;
    }

    // Apri SUBITO sull'ultima posizione nota (niente flash su Roma): se il device risponde, la raffino.
    const cached = this.store.get<[number, number]>(_.GEOLOCATION_KEY);
    let pos: [number, number] = this.isValidMapCenter(cached) ? cached : DEFAULT_MAP_CENTER;

    // Se riesco a prendere le coordinate dal device, punto la mappa su quelle
    try {
      let geopos: Geoposition | undefined;
      if (this.native) {
        if (this.hasNativeLocationPermission()) {
          geopos = await this.geoLocationNative.getCurrentPosition();
        } else {
          console.log("Map geolocation skipped: native location permission not granted");
        }
      } else {
        geopos = await this.geolocation.getCurrentPosition();
      }
      if (geopos) {
        pos = [geopos.coords.longitude, geopos.coords.latitude];
      }
    } catch {
      console.log("Autorizzazione non concessa");
    }

    this._center = pos;
    if (pos[0] !== DEFAULT_MAP_CENTER[0] || pos[1] !== DEFAULT_MAP_CENTER[1]) {
      this.store.set(_.GEOLOCATION_KEY, pos);
    }

    if (this.native && this.hasNativeLocationPermission()) {
      try {
        this._nativeWatchId = await Geo.watchPosition({ enableHighAccuracy: false, maximumAge: 5000, timeout: 10000 }, (position, err) => {
          if (err) {
            console.log("map component -> native watch position unavailable", err);
            return;
          }
          if (position) {
            const pos: Geoposition = <Geoposition>position;
            console.log("map component -> watch position")
            const coords: [number, number] = [pos.coords.longitude, pos.coords.latitude];
            if (this.isValidMapCenter(coords)) {
              this.store.set(_.GEOLOCATION_KEY, coords);
            }
          }
        });
      } catch (err) {
        console.log("Unable to start native geolocation watch", err);
      }
    } else if (this.native) {
      console.log("Map native geolocation watch skipped: permission not granted");
    } else {
      this._webPositionSubscription = this.geolocation.watchPosition().subscribe(async (data: Geoposition | PositionError) => {
        console.log("geolocation.watchPosition");
        if ((<PositionError>data).code) {
          this.toast.presentBasicToast({
            // header: 'Abilitare la localizzazione',
            header: this.translate.instant('TOAST_MESSAGES.ENABLE_LOCALIZATION_TITLE'),
            message: this.translate.instant('TOAST_MESSAGES.ENABLE_LOCALIZATION_MESSAGE'),
            color: 'secondary',
            icon: 'location-outline'
          });
        }
        else {
          const pos: Geoposition = <Geoposition>data;
          console.log("map component -> watch position")
          const coords: [number, number] = [pos.coords.longitude, pos.coords.latitude];
          if (this.isValidMapCenter(coords)) {
            this.store.set(_.GEOLOCATION_KEY, coords);
          }
        }
      });
    }

    console.log("map ngOnInit end");
    this.createMap();
  }

  ngAfterViewInit(): void {
    this._viewReady = true;
    this.createMap();
  }

  private async configureMapProvider(): Promise<void> {
    const config = await this.frontendConfigService.getConfig();
    const mapConfig = config?.map;
    this._fallbackStyleUrl = mapConfig?.fallbackStyleUrl || LEGACY_MAP_STYLE_URL;

    if (mapConfig?.provider === 'mapbox' && mapConfig.mapbox?.accessToken) {
      this._mapProvider = 'mapbox';
      this._clusterTextFont = ['Open Sans Regular'];
      mapboxgl.accessToken = mapConfig.mapbox.accessToken;
      this._style = this.createMapboxStyleUrl(mapConfig.mapbox);
      return;
    }

    this.useLegacyMap();
  }

  private useLegacyMap(): void {
    this._mapProvider = 'legacy';
    this._clusterTextFont = ['Noto Sans Regular'];
    this._style = this._fallbackStyleUrl || LEGACY_MAP_STYLE_URL;
  }

  private createMapboxStyleUrl(mapboxConfig: IFrontendMapboxConfig): string {
    const styleId = this.normalizeMapboxStyleId(mapboxConfig.styleId);

    if (styleId.startsWith('mapbox://') || styleId.startsWith('http://') || styleId.startsWith('https://')) {
      return styleId;
    }

    return `mapbox://styles/${styleId}`;
  }

  private normalizeMapboxStyleId(styleId: string): string {
    const normalized = (styleId || MAPBOX_DEFAULT_STYLE_ID).trim();

    if (normalized.startsWith('mapbox://') || normalized.startsWith('http://') || normalized.startsWith('https://')) {
      return normalized;
    }

    return normalized.replace(/^\/+|\/+$/g, '');
  }

  private isValidMapCenter(pos: Position | [number, number] | undefined | null): pos is [number, number] {
    return Array.isArray(pos)
      && pos.length >= 2
      && Number.isFinite(Number(pos[0]))
      && Number.isFinite(Number(pos[1]))
      && Math.abs(Number(pos[0])) <= 180
      && Math.abs(Number(pos[1])) <= 90;
  }

  private hasNativeLocationPermission(): boolean {
    return this.geoLocationNative?.permissionState?.location === 'granted';
  }

  private isAdmin(): boolean {
    return !!this.mainStateService.getUser()?.isAdmin;
  }

  private fallbackToLegacyMap(reason: string): void {
    if (this._mapProvider !== 'mapbox' || this._mapboxFallbackTriggered) return;

    console.log(`Mapbox error detected (${reason}), switching to legacy map fallback`);
    this._mapboxFallbackTriggered = true;
    this.globalState.set({ mapLoaded: false });
    this.hideGeolocate();
    this._mapProvider = 'legacy';
    this._clusterTextFont = ['Noto Sans Regular'];
    this._style = this._fallbackStyleUrl || LEGACY_MAP_STYLE_URL;
    this.destroyMapInstance();
    setTimeout(() => this.createMap());
  }

  private isMapboxBaseError(evt: any): boolean {
    const message = `${evt?.error?.message || evt?.message || ''}`;
    return message.indexOf('api.mapbox.com') >= 0 ||
      message.toLowerCase().indexOf('mapbox') >= 0 ||
      message.toLowerCase().indexOf('unauthorized') >= 0;
  }

  private shouldForceMapboxFallback(): boolean {
    if (environment.production) return false;

    const fromUrl = new URLSearchParams(window.location.search).get('forceMapboxFallback');
    const fromStorage = window.localStorage.getItem(FORCE_MAPBOX_FALLBACK_STORAGE_KEY);

    return fromUrl === '1' || fromStorage === '1';
  }

  private createMap(): void {
    if (!this._viewReady || !this._style || this._map || this._mapInitializing) return;

    this._mapInitializing = true;
    this.globalState.set({ mapLoaded: false });

    try {
      if (this._mapProvider === 'mapbox' && this.shouldForceMapboxFallback()) {
        throw new Error('Forced Mapbox fallback for local testing');
      }

      const map = new mapboxgl.Map({
        container: this.mapContainer.nativeElement,
        style: this._style,
        center: this._center,
        zoom: this._zoom,
        attributionControl: true,
        language: this.getMapLanguage(),
        renderWorldCopies: false
      });

      this._map = map;

      map.on('load', () => {
        this._mapInitializing = false;
        this.onLoad(map);
      });

      map.on('move', (evt) => this.onMove(evt));
      map.on('error', (evt) => {
        console.log('MAP -> A error event occurred.', evt);
        if (this.isMapboxBaseError(evt)) {
          this.fallbackToLegacyMap('runtime map error');
        }
      });
    } catch (err) {
      this._mapInitializing = false;
      console.log('Unable to create Mapbox map', err);
      if (this._mapProvider === 'mapbox') {
        this.fallbackToLegacyMap('map initialization error');
      }
    }
  }

  private destroyMapInstance(): void {
    this.hideGeolocate();
    this._selectedStationMarker = undefined;
    this._locationMarker = undefined;

    if (this._map) {
      try {
        this._map.remove();
      } catch { }
    }

    this._map = undefined;
    this._mapInitializing = false;
    this.geoLocateCtrlHide = true;
  }

  private updateMapDataSource(): void {
    const source = this._map?.getSource(DATA_SOURCE_ID) as GeoJSONSource;
    if (source?.setData) {
      source.setData(this._data);
    }
  }

  private loadMapImage(url: string): Promise<HTMLImageElement | ImageBitmap | ImageData> {
    return new Promise((resolve, reject) => {
      this._map.loadImage(url, (err, image) => {
        if (err || !image) {
          reject(err || new Error(`Unable to load image ${url}`));
          return;
        }

        resolve(image);
      });
    });
  }

  private normalizeOperatorId(value?: string | null): string | null {
    const normalized = (value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return normalized.length ? normalized : null;
  }

  private getPrimaryFeatureOperatorId(properties?: InstallationProperties): string | null {
    for (const operatorId of properties?.operatorIds || []) {
      const normalized = this.normalizeOperatorId(operatorId);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private getFallbackMarkerImageId(properties?: InstallationProperties): string {
    return `${properties?.cpo || 'not_yfl'}_${Number(properties?.state ?? 0)}`;
  }

  private getDynamicMarkerKey(operatorId: string, state: number): string {
    return `${operatorId}:${state}`;
  }

  private getDynamicMarkerImageId(operatorId: string, state: number): string {
    return `dynamic_${operatorId.toLowerCase()}_${state}`;
  }

  private getDynamicMarkerFileName(operatorId: string, state: number): string {
    return `marker-${operatorId.toLowerCase()}-${state}.png`;
  }

  private getDynamicMarkerUrl(operatorId: string, state: number): string {
    return this.HIO.endPoint(`multimedia/image/${this.getDynamicMarkerFileName(operatorId, state)}`);
  }

  private applyMarkerImagesToFeatures(): void {
    for (const feature of this._data.features || []) {
      if ((feature.properties as ClusterProperties)?.count) {
        continue;
      }

      const properties = (feature.properties || {}) as InstallationProperties;
      const operatorId = this.getPrimaryFeatureOperatorId(properties);
      const state = Number(properties.state ?? 0);
      const dynamicKey = operatorId ? this.getDynamicMarkerKey(operatorId, state) : null;
      const dynamicImageId = operatorId ? this.getDynamicMarkerImageId(operatorId, state) : null;

      properties.markerImage =
        dynamicKey
        && dynamicImageId
        && this._dynamicMarkerAvailability.get(dynamicKey)
        && this._map?.hasImage(dynamicImageId)
          ? dynamicImageId
          : this.getFallbackMarkerImageId(properties);
    }
  }

  private async ensureDynamicMarkerImages(): Promise<void> {
    if (!this._map) {
      return;
    }

    const pendingLoads: Promise<void>[] = [];
    for (const feature of this._data.features || []) {
      if ((feature.properties as ClusterProperties)?.count) {
        continue;
      }

      const properties = feature.properties as InstallationProperties;
      const operatorId = this.getPrimaryFeatureOperatorId(properties);
      const state = Number(properties?.state ?? 0);
      if (!operatorId || state < 0 || state > 5) {
        continue;
      }

      pendingLoads.push(this.ensureDynamicMarkerImage(operatorId, state));
    }

    if (pendingLoads.length === 0) {
      return;
    }

    await Promise.all(pendingLoads.map((load) => load.catch(() => undefined)));
    this.applyMarkerImagesToFeatures();
    this.updateMapDataSource();
  }

  private async ensureDynamicMarkerImage(operatorId: string, state: number): Promise<void> {
    const key = this.getDynamicMarkerKey(operatorId, state);
    const imageId = this.getDynamicMarkerImageId(operatorId, state);

    if (this._map?.hasImage(imageId)) {
      this._dynamicMarkerAvailability.set(key, true);
      return;
    }

    if (this._dynamicMarkerAvailability.get(key) === false) {
      return;
    }

    const pendingLoad = this._dynamicMarkerLoads.get(key);
    if (pendingLoad) {
      return pendingLoad;
    }

    const loadPromise = (async () => {
      try {
        const image = await this.loadMapImage(this.getDynamicMarkerUrl(operatorId, state));
        if (this._map && !this._map.hasImage(imageId)) {
          this._map.addImage(imageId, image, { sdf: false });
        }
        this._dynamicMarkerAvailability.set(key, true);
      } catch {
        this._dynamicMarkerAvailability.set(key, false);
      } finally {
        this._dynamicMarkerLoads.delete(key);
      }
    })();

    this._dynamicMarkerLoads.set(key, loadPromise);
    return loadPromise;
  }

  private getMapLanguage(): string {
    return this.normalizeMapLanguage(this.translateConfigService.forceGetCurrentLanguage());
  }

  private normalizeMapLanguage(lang: string): string {
    return lang === 'it' ? 'it' : 'en';
  }

  async onLoad(mapInstance: Map) {
    console.log("MAP.COMPONENT:onLoad")
    this.globalState.set({ mapLoaded: false });

    this._map = mapInstance;
    this._map.setRenderWorldCopies(false);

    if (!this._map.getSource(DATA_SOURCE_ID)) {
      this._map.addSource(DATA_SOURCE_ID, {
        type: 'geojson',
        data: this._data
      });
    }

    await this._map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: DATA_SOURCE_ID,
      slot: 'top',
      filter: ['has', 'count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'count'],
          '#fff',
          10, '#efefff',
          20, '#dfdfff',
          30, '#cfcfff',
          40, '#4f4',
          50, '#ddf',
          60, '#aaf',
          70, '#88f',
          80, '#44f',
          90, '#fede71',
          100, '#fdd',
          125, '#faa',
          150, '#f88',
          175, '#f44',
          200, '#ffd',
          300, '#ffa',
          400, '#ff8',
          500, '#ff4',
          750, '#999',
          1000, '#777',
          2000, '#555'   // tutte quelle con count maggiore a 20 hanno sfondo '#aaaaff'
        ],
        'circle-radius': [
          'step',
          ['get', 'count'],
          15,     // tutte quelle con count inferiore a 10 hanno raggio 15
          499, 20,
          999, 25,  // tutte quelle con count maggiore a 10 hanno raggio 20
          1999, 30   // tutte quelle con count maggiore a 20 hanno raggio 25
        ],
        'circle-stroke-color': '#111111',
        'circle-stroke-opacity': 0.25,
        'circle-stroke-width': 1.5
      }
    });

    await this._map.addLayer({
      id: 'clusters-count',
      type: 'symbol',
      source: DATA_SOURCE_ID,
      slot: 'top',
      filter: ['has', 'count'],
      layout: {
        'text-field': '{count}',
        'text-font': this._clusterTextFont,
        'text-size': 13,
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#111111',
        'text-halo-color': '#ffffff',
        'text-halo-width': 2,
        'text-halo-blur': 0.25
      }
    });

    //Caricamento immagini
    await Promise.all(this._staticMarkerImageIds.map(async (image_name) => {
      if (this._map.hasImage(image_name)) return;

      try {
        const image = await this.loadMapImage(`assets/stations/markers/${image_name}.png`);
        this._map.addImage(image_name, image, { sdf: false });
      } catch (err) {
        console.log(`Unable to load marker image ${image_name}`, err);
      }
    }));

    this.applyMarkerImagesToFeatures();
    await this.ensureDynamicMarkerImages();

    await this._map.addLayer({
      id: 'installations',
      type: 'symbol',
      source: DATA_SOURCE_ID,
      slot: 'top',
      filter: ['!has', 'count'],
      layout: {
        "icon-image": "{markerImage}",
        "icon-size": 0.6,
        "icon-allow-overlap": true,
        "icon-anchor": "bottom",
      },
    });

    let debounceClick;

    this._map.on('click', (e) => {
      this.ngZone.run(() => {
        debounceClick = setTimeout(() => {
          console.log('A click BACKGROUND event was emitted', e);
          this.clickItem.emit({ type: MapItemType.BACKGROUND, geometry: { type: "Point", coordinates: [e.lngLat.lng, e.lngLat.lat] } })
        }, 100);
      });
    });

    this._map.on('click', 'clusters', (e) => {
      this.ngZone.run(() => {
        if (debounceClick) clearTimeout(debounceClick);
        console.log('A click clusters event has occurred', e);
        this.clickItem.emit({ type: MapItemType.CLUSTER, geometry: e.features[0].geometry, properties: <ClusterProperties>e.features[0].properties })
      });
    });

    this._map.on('click', 'installations', (e) => {
      this.ngZone.run(() => {
        if (debounceClick) clearTimeout(debounceClick);
        const installation = <InstallationProperties>e.features[0].properties;
        //Molto importante per prendere i gruppi di punti vicini?
        // const features = this._map.queryRenderedFeatures({layers: ['installations']});
        // if(features.length) {
        //   if(features[0].id === installation.id) {
        //     const s = this._data.features.find(f => f.id === installation.id);
        //   }
        // }
        // const index = this._data.features.findIndex(x => x.properties.id === installation.id);
        // features[0].layer.layout.visibility = 'none';
        // this._data.features[index].properties.cpo = 'not_yfl'
        // this._map.setLayoutProperty('installations', 'icon-image',
        //     [
        //       'match',
        //       ['propierties']['id'], // get the feature id (make sure your data has an id set or use generateIds for GeoJSON sources
        //       this._data.features[index].properties.id, 'enx', //image when id is the clicked feature id
        //        'not_yfl' // default
        //     ]
        // )
        this.showSelectedMarker(installation.cpo, installation.state as StateValues, (<any>e.features[0].geometry).coordinates);
        //Puo' essere utile?
        // this._map.flyTo({
        //   center: [(<any>e.features[0]._geometry).coordinates[0], (<any>e.features[0]._geometry).coordinates[1]  - (2/1000)],
        //   zoom: 15,
        //   essential: true
        // })
        console.log('A click INSTALLATION event was emitted', installation, e.features[0]);
        this.clickItem.emit({ type: MapItemType.INSTALLATION, geometry: e.features[0].geometry, properties: installation });
      });
    });

    /*
    this._map.on('idle', function () {
      console.log('MAP -> A idle event occurred.');
    });
*/

    this._map.on('mouseenter', 'clusters', () => { this._map.getCanvas().style.cursor = 'pointer'; });
    this._map.on('mouseleave', 'clusters', () => { this._map.getCanvas().style.cursor = ''; });

    this._map.on('mouseenter', 'installations', () => { this._map.getCanvas().style.cursor = 'pointer'; });
    this._map.on('mouseleave', 'installations', () => { this._map.getCanvas().style.cursor = ''; });

    const station_selector = document.createElement('div');
    station_selector.style.width = "29px";
    station_selector.style.height = "34px";
    station_selector.style.border = "1px solid blue";
    station_selector.style.borderRadius = "6px";
    station_selector.style.visibility = "hidden";
    this._selectedStationMarker = new Marker({ anchor: 'bottom', element: station_selector }).setLngLat(<LngLatLike>this._center).addTo(this._map);

    // Disabilito la rotazione dalla mappa perchè crea problemi
    this._map.dragRotate.disable();
    this._map.touchZoomRotate.disableRotation();

    this._map.resize();

    // Override admin attivo → mostro il controllo ma NON seguo il device (resto sulla posizione scelta).
    this.showGeolocate(!this._startOverrideActive);
    this.manageSelectedInstallation(this.selectedInstallation);

    let self = this;

    var interval = setInterval(function () {
      if (!self._map) {
        clearInterval(interval);
        return;
      }
      var mapLoaded = self._map.loaded();
      if (mapLoaded) {
        clearInterval(interval);
        self.globalState.set({ mapLoaded: true });
        self.mapReady.emit(true);
        self.reloadBounds();
        console.log("MAP.COMPONENT:map loaded!");
      }
    }, 500);
  }

  onClick(evt) {
    console.log('map click', evt);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this._webPositionSubscription?.unsubscribe();
    this._languageSubscription?.unsubscribe();
    if (this._nativeWatchId) {
      Geo.clearWatch({ id: this._nativeWatchId });
    }
    this.destroyMapInstance();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.updates) {
      const sps: InstallationProperties[] = changes.updates.currentValue;
      if (sps.length === 0) return;

      if (this._data) {
        for (const sp of sps) {
          const s = this._data.features.find(f => f.properties.id === sp.id);
          if (s) {
            s.properties.state = sp.state;
          }
        }
        this._data = { ...this._data };
        this.applyMarkerImagesToFeatures();
        this.updateMapDataSource();
        void this.ensureDynamicMarkerImages();
      }
    }

    if (changes.data) {
      this._data = changes.data.currentValue;
      this.applyMarkerImagesToFeatures();
      this.updateMapDataSource();
      void this.ensureDynamicMarkerImages();
    }

    if (changes.center && !!changes.center.currentValue) {

      console.log("changes.center", changes.center.currentValue);
      // Utilizzato per la ricerca: quando inserisco la via, mi restituisce le coordinate lat lng e in quel caso metto un marker rosso

      if (this._locationMarker) this._locationMarker.remove();
      const c: MapPosition = changes.center.currentValue;
      this._center = [c.lng, c.lat];

      if (this._map) {
        if (c.showLocationMarker) {

          let locationMarker = document.createElement('div');
          locationMarker.classList.add('location_marker');

          const iconName = 'location_marker.svg';

          locationMarker.style.backgroundImage = `url('./../../../assets/app-icons/${iconName}')`;
          locationMarker.style.width = '29px';
          locationMarker.style.height = '34px';
          locationMarker.style.cursor = 'pointer';
          locationMarker.style.opacity = '1';

          this._locationMarker = new Marker({ element: locationMarker }).setLngLat([c.lng, c.lat])
            .addTo(this._map);
        }

        this._map.flyTo({ center: [c.lng, c.lat], zoom: 15 });
      }
    }

    if (changes.selectedInstallation) {
      this.manageSelectedInstallation(changes.selectedInstallation.currentValue);
    }
  }

  followMe() {
    const delay = 1000;
    if (!this.native) {
      setTimeout(() => {
        this.geoLocateCtrl?.trigger();
      }, delay)
    }
    if (this.native && this.geoLocationNative?.permissionState?.location === 'granted') {
      setTimeout(() => {
        this.geoLocateCtrl.trigger();
      }, delay)
    }
  }

  // Evento Move è sollevato ogni volta cambia l'inquadratura della mappa (sia per traslazione che zoom)
  onMove(evt: any) {
    if (this._dbto) clearTimeout(this._dbto); // debounce
    this._dbto = setTimeout(() => {
      this.reloadBounds();
    }, environment.MAP_UPDATE_DEBOUNCE_MS);
  }

  reloadBounds() {
    const bounds = this.getBounds(environment.MAP_FRAME_PERC);
    this._shownMap = bounds;
    //if (this._shownMap) {
    this.mapBounds.emit(bounds);
    // }
  }

  onGeolocate(position: any) {
    console.log('geolocate', position);
  }

  // frame è quello che voglio in più in percentuale
  getBounds(frame: number): IMapBounds {
    if (!this._map) return;

    const canvas = this._map.getCanvas();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const wF = w * frame / 2;
    const hF = h * frame / 2;
    const c00 = this._map.unproject([-wF, -hF]).toArray();
    const cWH = this._map.unproject([w + wF, h + hF]).toArray();

    const bounds = {
      lat: { min: Math.min(c00[1], cWH[1]), max: Math.max(c00[1], cWH[1]) },
      lng: { min: Math.min(c00[0], cWH[0]), max: Math.max(c00[0], cWH[0]) },
      zoom: this._map.getZoom()
    };
    return bounds;
  }

  manageSelectedInstallation(installation: IInstallation) {
    if (!this._map || !this._selectedStationMarker) return;

    if (installation) {
      const installationMarkerAlreadyInMap = this._data.features.find(f => f.properties.id === installation.id);
      if (!installationMarkerAlreadyInMap) {
        // Se l'installazione proviene da un deeplink devo disabilitare il geolocate altrimenti la mappa viene riportata alla localizzazione corrente
        if (this.globalState.get("installationByDeeplink")) {
          this.showGeolocate(false);
          this.globalState.set({ installationByDeeplink: false });
        }

        this._map.stop();
        this._map.jumpTo({ center: [installation.coord.coordinates[0], installation.coord.coordinates[1]], padding: { left: 0, top: 0, bottom: 300, right: 0 }, zoom: 15 });
        this.showSelectedMarker(null, installation.state, installation.coord.coordinates);
      }
    }
    else {
      this.hideSelectedMarker();
    }
  }

  showSelectedMarker(cpo: string, state: StateValues, coordinates: Position) {
    if (!this._selectedStationMarker) return;
    this._selectedStationMarker.setLngLat([coordinates[0], coordinates[1]]);
    this._selectedStationMarker.getElement().style.visibility = "visible";
  }

  hideSelectedMarker() {
    if (this._selectedStationMarker) this._selectedStationMarker.getElement().style.visibility = "hidden";
  }

  public showGeolocate(trackUserLocation: boolean = true) {
    this.hideGeolocate();
    let self = this;

    this.geoLocateCtrl = new mapboxgl.GeolocateControl({
      showUserLocation: true,
      showAccuracyCircle: false,
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: trackUserLocation
    });

    this.geoLocateCtrl.on('geolocate', function () {
      var userlocation = self.geoLocateCtrl._lastKnownPosition;
      if (userlocation) {
        const coords: [number, number] = [userlocation.coords.longitude, userlocation.coords.latitude];
        if (self.isValidMapCenter(coords)) {
          self.store.set(_.GEOLOCATION_KEY, coords);
        }
      }
    });

    this.geoLocateCtrl.on('error', async (e) => {
      if (this.native) {
        this.toast.presentBasicToast({
          header: 'Abilitare la localizzazione',
          message: 'Abilitando la localizzazione possiamo mostrarti i punti di ricarica più vicini',
          color: 'secondary',
          duration: 6000,
          icon: 'location-outline'
        });
      }
    })

    if (this.geoLocateCtrlHide) {
      var interval = setInterval(function () {
        if (!self._map) {
          clearInterval(interval);
          return;
        }
        var mapLoaded = self._map.loaded();
        if (mapLoaded) {
          clearInterval(interval);
          self.geoLocateCtrlHide = false;
          self._map.addControl(self.geoLocateCtrl, 'bottom-right');
          if (trackUserLocation) {
            self.followMe();
          }
        }
      }, 500);
    }
  }

  hideGeolocate() {
    if (this.geoLocateCtrl && !this.geoLocateCtrlHide) {
      this.geoLocateCtrlHide = true;
      try {
        this._map.removeControl(this.geoLocateCtrl)
      }
      catch { }
    }
  }
}
